/**
 * TappingPointsTableV2 — Tapping Points list, ported from the
 * prototype's `.screen-points` (cms-redesign-mockup.html ~8746+,
 * tp-grid / tp-card styles 3085–3209).
 *
 * Layout:
 *   • `.tp-grid` — repeat(auto-fill, minmax(200px, 1fr)) of `.tp-card`s.
 *     Default card (tapping_point_is_default = 1) renders first.
 *   • Each card:
 *       – 3:4 portrait art on a navy background. Two stacked layers:
 *         the avatar man (hover-revealed, opacity 0 → 1) underneath
 *         and the tapping_point_image_url overlay (always on top).
 *         The composite mirrors how the mobile app renders these
 *         poses at runtime.
 *       – "DEFAULT" coral pin top-right when is_default.
 *       – "App preview" subtle hover-revealed pill bottom-left
 *         (suppressed on the default row — it IS the avatar).
 *       – Body: tapping_point_name + always-visible edit icon button.
 *
 * No search filter (the list is ~11 entries; client-side search adds
 * noise without helping). No status filter — these don't have an
 * is_active flag, only is_default.
 *
 * Data source: GET /tapping-points (legacy `{objects, count}` envelope)
 * via the existing `fetchTappingPoints` action.
 */
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchTappingPoints } from '../../../actions/tappingPointsActions';
import { IconButton, cn } from '../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

/* ─── Single tapping point card ────────────────────────────────────── */

function TappingPointCard({ tp }) {
  const isDefault = !!tp.tapping_point_is_default;
  return (
    <Link
      to={`/tapping-points/edit/${tp.tapping_point_id}`}
      className={cn(
        'group block bg-surface border border-sand no-underline overflow-hidden',
        'transition-all duration-200',
        // Default row is static — it IS the avatar, so the lift /
        // border-tint / inner avatar-swap hover all make less sense.
        // Only the edit affordance still reacts to hover.
        !isDefault && 'hover:border-teal-green',
      )}
      style={{
        color: 'inherit',
        borderRadius: 12,
        position: 'relative',
        minWidth: 0,
      }}
      onMouseEnter={isDefault ? undefined : (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow =
          '0 10px 24px #2C3E5018, 0 2px 6px #2C3E500C';
      }}
      onMouseLeave={isDefault ? undefined : (e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* `.tp-art` — 3:4 portrait on navy. Two stacked layers (per
       *  prototype 3117–3160):
       *    1) Avatar man — hover-revealed beneath, z:0.
       *    2) tapping_point_image_url — always on top, z:1, the
       *       dot-only overlay that the app composites at runtime.
       *  The default row IS the avatar (no point markers to layer
       *  over it), so its hover layers are suppressed. */}
      <div
        className="bg-navy"
        style={{
          aspectRatio: '3 / 4',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {!isDefault && (
          <div
            aria-hidden
            className="opacity-0 group-hover:opacity-100"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: "url('/img/avatar_man_large.webp')",
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              backgroundRepeat: 'no-repeat',
              backgroundColor: '#2C3E50', /* navy fallback */
              transition: 'opacity 220ms ease',
              zIndex: 0,
            }}
          />
        )}
        {tp.tapping_point_image_url && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url('${tp.tapping_point_image_url}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              backgroundRepeat: 'no-repeat',
              zIndex: 1,
            }}
          />
        )}

        {/* `.tp-hover-label` — small frosted 'App preview' pill that
         *  fades in with the avatar layer so the composite reads as
         *  intentional. Suppressed on the default row. */}
        {!isDefault && (
          <span
            aria-hidden
            className={cn(
              'font-condensed font-bold uppercase text-white',
              'opacity-0 group-hover:opacity-100',
            )}
            style={{
              position: 'absolute',
              left: 8,
              bottom: 8,
              padding: '2px 8px',
              borderRadius: 999,
              background: '#00000080',
              backdropFilter: 'blur(3px)',
              fontSize: 9.5,
              letterSpacing: '0.08em',
              transition: 'opacity 200ms ease, transform 200ms ease',
              zIndex: 3,
            }}
          >
            App preview
          </span>
        )}

        {isDefault && (
          <span
            aria-hidden
            className="font-condensed font-bold uppercase text-white"
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              padding: '3px 9px',
              borderRadius: 999,
              background: '#E58A6A', /* coral */
              fontSize: 10,
              letterSpacing: '0.08em',
              boxShadow: '0 2px 6px #00000028',
              zIndex: 2,
            }}
          >
            Default
          </span>
        )}
      </div>

      {/* Body — name + edit affordance. */}
      <div
        className="flex items-center justify-between"
        style={{ padding: '10px 12px', gap: 8 }}
      >
        <span
          className="font-semibold text-navy truncate min-w-0"
          style={{ fontSize: 14 }}
          title={tp.tapping_point_name || ''}
        >
          {tp.tapping_point_name || 'Untitled'}
        </span>
        {/* IconButton inside a Link nests <a><button> — we don't have
         *  a real action here other than the wrapping link, so render
         *  a visual edit glyph (not a real button) to avoid double
         *  click targets / a11y traps. */}
        <span
          aria-hidden
          className="grid place-items-center text-text-muted group-hover:text-navy group-hover:bg-cream"
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            border: '1px solid transparent',
            transition: 'all 140ms ease',
            flexShrink: 0,
          }}
        >
          {EditIconSvg}
        </span>
      </div>
    </Link>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function TappingPointsTableV2() {
  const dispatch = useDispatch();
  const items = useSelector((s) => s.tapping_points.tapping_points);

  useEffect(() => {
    dispatch(fetchTappingPoints());
  }, [dispatch]);

  // Default first (prototype: "Rendered first"), then everything else
  // in API order. This way the fallback frame anchors the grid.
  const ordered = useMemo(() => {
    if (!Array.isArray(items)) return null;
    const def = items.filter((tp) => tp.tapping_point_is_default);
    const rest = items.filter((tp) => !tp.tapping_point_is_default);
    return [...def, ...rest];
  }, [items]);

  if (!ordered) {
    return (
      <div
        className="flex items-center justify-center text-text-muted"
        style={{ height: 256 }}
      >
        Loading tapping points…
      </div>
    );
  }

  if (ordered.length === 0) {
    return (
      <div
        className="bg-surface border border-sand text-text-muted italic text-center"
        style={{
          borderRadius: 12,
          padding: '40px 16px',
          fontSize: 13,
          margin: '0 28px 32px',
        }}
      >
        No tapping points yet — add one to get started.
      </div>
    );
  }

  return (
    <div style={{ padding: '0 28px 32px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gridAutoRows: 'max-content',
          gap: 16,
          alignContent: 'start',
        }}
      >
        {ordered.map((tp) => (
          <TappingPointCard key={tp.tapping_point_id} tp={tp} />
        ))}
      </div>
    </div>
  );
}
